import React, {ReactElement} from 'react';
import {Box} from '@mui/material';
import Carousel from 'shared/components/carousel';
import CollegeCard from 'src/components/CollegeCard';
import {
  ApplicationStatusObject,
  ObjectOf,
  RecommendationBucket as StudentRecommendationBucket,
  Store,
} from 'src/types/store';
import classNames from 'classnames';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {FollowActions} from 'src/actions/studentCollege';
import {useSelector} from 'react-redux';
import {RecommendedCollege} from 'src/services/RecommendationsService';
import styles from './recommendation-bucket.module.scss';

interface ClassMap {
  title: string;
  description: string;
  section: string;
}

export type RecommendationBucketProps = FollowActions & {
  recommendationsShownId: string;
  recommendationBucket: StudentRecommendationBucket;
  isSenior?: boolean;
  collegeApplicationStatuses: ObjectOf<ApplicationStatusObject>;
  setCollegeAppStatus: (collegeId: string, status: string, segmentEventLocation?: string) => void;
  classes?: Partial<ClassMap>;
  wideCards?: boolean;
  followCollege: (id: string) => void;
  unfollowCollege: (id: string) => void;
};

export const getBucketColleges =
  (collegeIds) =>
  (state: Store): RecommendedCollege[] =>
    collegeIds.map((id) => state.recommendations.collegeInformation[id]);

export default function RecommendationBucket(props: RecommendationBucketProps): ReactElement {
  const mq = useMediaQuery();
  const recommendation = props.recommendationBucket;
  const showBasicContainer = mq.MS_AND_DOWN || recommendation.collegeIds.length < 3;
  const classes = props.classes || ({} as ClassMap);
  const colleges = useSelector<Store, RecommendedCollege[]>(
    getBucketColleges(recommendation.collegeIds)
  );

  const cards = colleges.map(
    (college): ReactElement => (
      <CollegeCard
        key={`card-${recommendation.id}-${college._id}`}
        college={college}
        collegeApplicationStatus={props.collegeApplicationStatuses[college._id]}
        followCollege={props.followCollege}
        recommendationId={recommendation.id}
        setApplicationStatus={props.setCollegeAppStatus}
        showApplicationStatusMenu
        unfollowCollege={props.unfollowCollege}
        events={{
          recommendationsShownId: props.recommendationsShownId,
          bucketEventId: recommendation.bucketShownEventId,
        }}
        baseProps={{
          condensed: true,
          showDeadline: props.isSenior || false,
          showStatus: true,
          wideCard: props.wideCards,
        }}
      />
    )
  );

  return (
    <section className={classNames('college-search-buckets-section', classes.section)}>
      <header className="college-search-buckets-title-container">
        <h3 className={styles.title} aria-label={recommendation.title}>
          {recommendation.title}
        </h3>
        <p className={styles.description}>{recommendation.description}</p>
      </header>
      <Box>
        {showBasicContainer ? (
          <div className="college-search-buckets-bucket-cards">{cards}</div>
        ) : (
          <Carousel>{cards}</Carousel>
        )}
      </Box>
    </section>
  );
}
